<script lang="ts">
  /** 1 フレームの盤面（components.md §1.7）。SVG ではなく HTML のリストで組む */
  import type { VizState } from '../../lib/viz';
  import { formatVizValue, vizEntries, vizItems } from './viz-format';

  interface Props {
    state: Record<string, VizState>;
    changed?: ReadonlySet<string>;
  }

  let { state, changed = new Set<string>() }: Props = $props();

  function marksAt(s: VizState, i: number): string[] {
    return Object.entries(s.marks ?? {})
      .filter(([, at]) => at === i)
      .map(([k]) => k);
  }
  function marksOutside(s: VizState): string[] {
    const n = vizItems(s).length;
    return Object.entries(s.marks ?? {})
      .filter(([, at]) => at < 0 || at >= n)
      .map(([k, at]) => `${k} = ${at}`);
  }
</script>

<div class="board" data-testid="viz-board">
  {#each Object.entries(state) as [label, s] (label)}
    <section class="var" class:changed={changed.has(label)} aria-label={label}>
      <h3 class="label">
        <code>{label}</code>
        {#if changed.has(label)}<span class="changed-tag">変化</span>{/if}
      </h3>
      {#if s.kind === 'value'}
        <span class="value num" data-testid="viz-value-{label}">{formatVizValue(s.value)}</span>
      {:else if s.kind === 'dict'}
        <table class="dict">
          <thead><tr><th scope="col">キー</th><th scope="col">値</th></tr></thead>
          <tbody>
            {#each vizEntries(s) as [k, v], i (i)}
              <tr><td>{formatVizValue(k)}</td><td>{formatVizValue(v)}</td></tr>
            {/each}
          </tbody>
        </table>
      {:else if s.kind === 'stack'}
        {@const items = vizItems(s)}
        <ol class="stack" aria-label="{label} の中身（下から上）">
          {#each items as v, i (i)}
            <li class="cell">
              {formatVizValue(v)}{#if i === items.length - 1}<span class="tag">一番上</span>{/if}
            </li>
          {/each}
        </ol>
      {:else}
        {@const items = vizItems(s)}
        <ol class="cells" aria-label="{label} の中身">
          {#each items as v, i (i)}
            {@const m = marksAt(s, i)}
            <li
              class="cell"
              class:done={s.kind === 'queue' && s.head !== undefined && i < s.head}
              aria-label="{i} 番目、値 {formatVizValue(v)}{m.length
                ? `、${m.join(' と ')}`
                : ''}{s.head === i ? '、先頭' : ''}"
            >
              <span class="idx num" aria-hidden="true">{i}</span>
              <span class="v">{formatVizValue(v)}</span>
              {#if m.length || s.head === i}
                <span class="tag" aria-hidden="true"
                  >{[...m, ...(s.head === i ? ['head'] : [])].join(' ')}</span
                >
              {/if}
            </li>
          {/each}
        </ol>
        {#if marksOutside(s).length}
          <p class="outside">範囲の外: {marksOutside(s).join('、')}</p>
        {/if}
      {/if}
    </section>
  {/each}
</div>

<style>
  .board {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);
  }
  .var {
    min-width: 6rem;
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    background: var(--surface);
    transition: border-color var(--dur-base) var(--ease-out);
  }
  .var.changed {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  .label {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--fs-sm);
    margin-bottom: var(--space-1);
  }
  .changed-tag,
  .tag {
    font-size: var(--fs-xs);
    color: var(--accent-soft-text);
    background: var(--accent-soft);
    padding: 0 var(--space-1);
    border-radius: var(--radius-xs);
  }
  .value {
    font-family: var(--font-mono);
    font-size: var(--fs-lg);
    font-weight: var(--fw-semibold);
  }
  .cells,
  .stack {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    gap: var(--space-1);
  }
  .stack {
    flex-direction: column-reverse;
  }
  .cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 2.5rem;
    padding: var(--space-1) var(--space-2);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-sm);
    font-family: var(--font-mono);
  }
  .stack .cell {
    flex-direction: row;
    gap: var(--space-2);
    justify-content: space-between;
  }
  .cell.done {
    opacity: 0.5;
  }
  .idx {
    font-size: var(--fs-xs);
    color: var(--text-soft);
  }
  .dict {
    border-collapse: collapse;
    font-family: var(--font-mono);
    font-size: var(--fs-sm);
  }
  .dict th,
  .dict td {
    padding: var(--space-1) var(--space-2);
    border: 1px solid var(--border);
    text-align: left;
  }
  .outside {
    font-size: var(--fs-xs);
    color: var(--text-soft);
  }
</style>
